/**
 * Plans, limits and top-ups in the world - the same rules and words as the portal (owner 2026-09-29;
 * Sol ~/.claude/sol-runs/quiklearn-guardrails/build-plan.verdict.md D7: a native screen in the player).
 * Pure functions so the rules are tested without the world. Prices are shown only; the server prices
 * every purchase.
 */

/** Two limit patterns: "Out of ___ / Resets <date>." and "___ requires a paid plan". "Chat" for text. */
export const meterName = meter => {
  if (meter === 'voice') return 'Voice minutes';
  if (meter === 'text' || meter === 'hybrid') return 'Chat minutes';
  if (meter === 'course') return 'Course generations';
  return 'minutes';
};

const longDate = value => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-US', {month: 'long', day: 'numeric', year: 'numeric'});
};

const shortDate = value => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', {month: 'short', day: 'numeric'});
};

const tutorMeter = meter => (meter === 'voice' ? 'voice' : 'text');

/**
 * The heading of the full-screen plans for what opened them (null: a plain "Upgrade your plan").
 * When one tutor meter is out and the other still has a minute or more, the quiet link under the
 * plans offers it ("Use Chat instead") - the approved limits preview, row 1.
 */
export const limitReason = (exhausted, usage) => {
  if (!exhausted) return null;
  if (exhausted.kind === 'locked') {
    return {tone: 'paid', icon: 'paid', title: `${exhausted.subject || 'This unit'} requires a paid plan`, line: null};
  }
  if (exhausted.kind !== 'quota') return null;
  const resets = shortDate(exhausted.resetAt || usage?.periodEnd);
  const meter = exhausted.meter === 'course' ? 'course' : exhausted.meter ? tutorMeter(exhausted.meter) : null;
  const reason = {
    tone: 'out',
    icon: meter === 'voice' ? 'voice' : meter === 'course' ? 'course' : 'chat',
    title: `Out of ${meterName(meter)}`,
    line: resets ? `Resets ${resets}.` : null,
  };
  if (meter === 'voice' || meter === 'text') {
    const other = meter === 'voice' ? 'text' : 'voice';
    const otherLeft = usage?.[other]?.remaining ?? (exhausted.otherMeter === other ? exhausted.otherSecondsLeft : 0);
    if ((otherLeft || 0) >= 60) {
      return {...reason, useInstead: other, dismissLabel: `Use ${other === 'voice' ? 'Voice' : 'Chat'} instead`};
    }
  }
  return reason;
};

/** Plans billed outside Stripe are changed where they are billed (the portal's managedElsewhereNote). */
export const managedElsewhereNote = managedBy =>
  managedBy === 'apple' ? 'Your plan is managed in the App Store.'
    : managedBy === 'google' ? 'Your plan is managed in Google Play.'
      : 'Your plan is managed by your school or teacher.';

/** Who bills the plan: 'stripe' plans are changed here; null on Free. */
export const billingManagedBy = subscription => {
  if (!subscription || isFreePlan(subscription)) return null;
  return subscription.billingManagedBy || (subscription.canCancel ? 'stripe' : 'other');
};

/** What the HUD needs: Free sees "Upgrade"; the top plan buys more instead of upgrading. */
export const planInfoFrom = (subscription, plans, catalog) => {
  const free = isFreePlan(subscription);
  const paid = (plans || []).filter(plan => plan.planKey !== 'free').sort((a, b) => a.monthlyPriceMinor - b.monthlyPriceMinor);
  return {
    subscription: subscription || null,
    plans: plans || [],
    catalog: catalog || null,
    isFree: free,
    onTopPlan: !free && paid.length > 0 && paid[paid.length - 1].planKey === subscription?.planKey,
    managedBy: billingManagedBy(subscription),
  };
};

/** The low-minutes pill's action (the approved preview): top plan buys more, everyone else upgrades. */
export const lowMinutesAction = (secondsLeft, info) => {
  if (!Number.isFinite(secondsLeft) || secondsLeft > 120 || !info) return null;
  // A plan billed by the App Store, Google Play or a school is changed there, but can still buy more.
  if (info.managedBy && info.managedBy !== 'stripe') return 'buyMore';
  return info.onTopPlan ? 'buyMore' : 'upgrade';
};

export const isFreePlan = subscription =>
  !subscription?.planKey || subscription.planKey === 'free' || subscription.isFree === true;

export const offerFor = (plan, period) =>
  (plan?.offers || []).find(offer => offer.available && offer.billingInterval === period);

export const minutes = seconds => Math.floor((seconds || 0) / 60);

/** What a plan card's one button does and says (portal planButtonLabel + periodActionFor). */
export const cardAction = ({plan, period, current, currentInterval, scheduled, isFree}) => {
  const offer = offerFor(plan, period);
  if (!offer) return {kind: 'unavailable', label: 'Not available yet'};
  // Free comes from the subscription, never from a missing catalog entry: a paid learner whose plan is
  // not in the catalog must change plan through the server, not start a second checkout (Sol
  // audit-player-plans).
  const onFree = isFree !== undefined ? Boolean(isFree) : !current || current.planKey === 'free';
  if (!onFree && !current) return {kind: 'change', offerId: offer.id, label: `Switch to ${plan.name}`};
  if (!onFree && current.planKey === plan.planKey && currentInterval === period) return {kind: 'current', label: 'Current plan'};
  if (scheduled && scheduled.planKey === plan.planKey && scheduled.billingInterval === period) {
    return {kind: 'scheduled', label: `Starts ${shortDate(scheduled.effectiveAt) || 'at renewal'}`};
  }
  if (onFree) return {kind: 'checkout', offerId: offer.id, label: `Upgrade to ${plan.name}`};
  if (current.planKey === plan.planKey) {
    return {kind: 'change', offerId: offer.id, label: `Switch to ${period === 'annual' ? 'yearly' : 'monthly'}`};
  }
  return {
    kind: 'change',
    offerId: offer.id,
    label: plan.monthlyPriceMinor > current.monthlyPriceMinor ? `Upgrade to ${plan.name}` : `Switch to ${plan.name}`,
  };
};

/**
 * Money as learners see it: US dollars read "US $9.99", exactly as in the portal (reviewer feedback via the
 * owner 2026-09-29; Sol currency-format A). Other currencies keep the standard format.
 */
export const money = (minor, currency = 'USD') => {
  const value = (minor || 0) / 100;
  if (String(currency).toUpperCase() === 'USD') {
    const digits = new Intl.NumberFormat('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2}).format(Math.abs(value));
    return `${value < 0 ? '-' : ''}US $${digits}`;
  }
  return new Intl.NumberFormat('en-US', {style: 'currency', currency}).format(value);
};

const periodName = period => (period === 'annual' ? 'yearly' : 'monthly');
const brandName = brand => (brand ? brand[0].toUpperCase() + brand.slice(1) : 'Card');
const planLabel = planKey => planKey.charAt(0).toUpperCase() + planKey.slice(1);

/**
 * The one-step confirmation for a subscriber: the portal's planChangeSummary (Sol
 * subscription-page Q1) - Due now / Starts / Next charge, notes only when relevant.
 */
export const changeSummary = (preview, planName, period = 'monthly') => {
  const cash = minor => money(minor, preview.currency);
  const target = `${planName} (${periodName(period)})`;
  const then = period === 'annual' ? 'then yearly' : 'then monthly';
  const notes = [];
  if (preview.replacesPending) {
    notes.push(`Replaces your scheduled change to ${planLabel(preview.replacesPending.planKey)} (${periodName(preview.replacesPending.billingInterval)}).`);
  }
  if (preview.chargeNow) {
    const amount = cash(preview.amountDueMinor);
    const charged = preview.amountDueMinor > 0;
    const card = charged && preview.card ? ` · ${brandName(preview.card.brand)} •••• ${preview.card.last4}` : '';
    // Unused time worth more than the new plan's first charge becomes credit on the next charges
    // (Sol reaudit-player-plans Job 2 A): the next charge is shown net of it, as Stripe will take it.
    const credit = preview.creditAppliedMinor || 0;
    const left = preview.creditRemainingMinor || 0;
    const nextAmount = preview.nextChargeMinor ?? preview.renewalAmountMinor;
    let next = 'None - auto-renewal is off';
    if (preview.renewalAmountMinor && preview.nextChargeAt && nextAmount != null) {
      const on = `${cash(nextAmount)} on ${longDate(preview.nextChargeAt)}`;
      next = credit === 0 ? `${on}, ${then}` : left === 0 ? `${on}, then ${cash(preview.renewalAmountMinor)} ${periodName(period)}` : on;
    }
    if (left > 0) notes.push(`Your remaining ${cash(left)} credit covers later charges.`);
    if (preview.renewalOff) notes.push('Auto-renewal stays off.');
    return {
      title: `Upgrade to ${target}`,
      rows: [
        {label: 'Due now', value: `${amount}${card}`},
        {label: 'Starts', value: 'Today'},
        ...(credit > 0 ? [{label: 'Credit applied', value: cash(credit)}] : []),
        {label: 'Next charge', value: next},
      ],
      notes,
      confirmLabel: charged ? `Pay ${amount}` : 'Confirm upgrade',
    };
  }
  return {
    title: `Switch to ${target}`,
    rows: [
      {label: 'Due now', value: cash(0)},
      {label: 'Starts', value: longDate(preview.effectiveAt)},
      {label: 'Charge then', value: preview.renewalAmountMinor ? `${cash(preview.renewalAmountMinor)}, ${then}` : '-'},
    ],
    notes,
    confirmLabel: 'Confirm change',
  };
};

/** The toast after a confirmed change: the server's words for an upgrade, the date for a scheduled one. */
export const changeResultLine = result =>
  result?.kind !== 'upgrade_now' && result?.effectiveAt ? `Your plan changes on ${longDate(result.effectiveAt)}.` : result?.message;

/** The server refused a stale quote; the learner is shown the new one instead. */
export const isStaleQuote = code => code === 'QUOTE_CHANGED' || code === 'QUOTE_EXPIRED';

/**
 * Top-up display pricing: the server's integer arithmetic exactly. Prices: Sol topup-per-meter (E) and
 * topup-bundle-cap - Voice 30 min $11.99, Chat 150 min $15.99, course $15.99, All three $35.99,
 * $4.99-$199.99 per purchase; steps: Sol topup-sliders.
 */
export const TOPUP_RULES = {
  voice: {label: 'Voice minutes', step: 1, max: 120, packUnits: 30, packMinor: 1199},
  text: {label: 'Chat minutes', step: 5, max: 600, packUnits: 150, packMinor: 1599},
  course: {label: 'Course generations', step: 1, max: 5, packUnits: 1, packMinor: 1599},
};
export const TOPUP_BUNDLE_MINOR = 3599;
export const TOPUP_MIN_MINOR = 499;
export const TOPUP_MAX_MINOR = 19999;
export const ALL_THREE = {voice: 30, text: 150, course: 1};
export const TOPUP_PRESETS = [
  {label: 'All three', value: ALL_THREE},
  {label: 'Voice', value: {voice: 30, text: 0, course: 0}},
  {label: 'Chat', value: {voice: 0, text: 150, course: 0}},
  {label: 'Course', value: {voice: 0, text: 0, course: 1}},
];
export const unitsMinor = (units, packUnits, packMinor) => Math.floor((units * packMinor * 2 + packUnits) / (packUnits * 2));

/** One meter's price on its own, as shown beside its slider. */
export const meterMinor = (meter, units) => unitsMinor(units, TOPUP_RULES[meter].packUnits, TOPUP_RULES[meter].packMinor);

export const quoteTopUp = sel => {
  const bundles = Math.min(Math.floor(sel.voice / 30), Math.floor(sel.text / 150), sel.course);
  let total = bundles * TOPUP_BUNDLE_MINOR;
  ['voice', 'text', 'course'].forEach(meter => {
    const rule = TOPUP_RULES[meter];
    const rest = sel[meter] - bundles * rule.packUnits;
    if (rest > 0) total += unitsMinor(rest, rule.packUnits, rule.packMinor);
  });
  const blocked = total < TOPUP_MIN_MINOR ? `Minimum ${money(TOPUP_MIN_MINOR)}`
    : total > TOPUP_MAX_MINOR ? `Maximum ${money(TOPUP_MAX_MINOR)} per purchase` : null;
  return {totalMinor: total, blocked};
};

/** "+30 Voice min · +150 Chat min · +1 course" for a paid top-up. */
export const topUpSummary = t =>
  [t.voiceMinutes > 0 ? `+${t.voiceMinutes} Voice min` : '', t.chatMinutes > 0 ? `+${t.chatMinutes} Chat min` : '',
    t.courses > 0 ? `+${t.courses} ${t.courses === 1 ? 'course' : 'courses'}` : ''].filter(Boolean).join(' · ');

const CONFIRMING = 'Confirming your payment';
const NOT_COMPLETED = {title: 'Payment not completed', description: 'Nothing was charged.', tone: 'neutral'};

/**
 * Back from a plan checkout (the portal's rule, Sol reaudit-portal-billing): success only when the
 * server shows that checkout is now the learner's plan; a long wait ends neutrally. Null: keep asking.
 */
export const planReturnMessage = (state, finalAttempt) => {
  if (state?.state === 'active') {
    const name = state.planKey ? `${state.planKey.charAt(0).toUpperCase()}${state.planKey.slice(1)}` : 'your new plan';
    return {title: `You’re on ${name}`, description: 'Your new plan is ready.', tone: 'good'};
  }
  if (state?.state === 'unpaid') return NOT_COMPLETED;
  return finalAttempt ? {title: CONFIRMING, description: 'Your plan will update here once it’s confirmed.', tone: 'neutral'} : null;
};

/** The same for a top-up: "More usage added" only once the purchase is PAID. */
export const topUpReturnMessage = (status, finalAttempt) => {
  if (status?.status === 'PAID') return {title: 'More usage added', description: topUpSummary(status), tone: 'good'};
  if (status?.status === 'EXPIRED' || status?.status === 'FAILED') return NOT_COMPLETED;
  return finalAttempt ? {title: CONFIRMING, description: 'Your extra usage will appear once it’s confirmed.', tone: 'neutral'} : null;
};
